import { useState } from "react";
import { useBarcodeScan } from "src/hooks/useBarcodeScan";
import usePDA1060_R01Store from "./usePDA1060_R01Store";
import PDA1060_R01Content from "./PDA1060_R01Content";

/** PDA1060_R01 재고 조회 입력 폼 (PDA1090_R01 스타일) */
const PDA1060_R01Info = () => {
  const { displayInfo, stockList } = usePDA1060_R01Store();
  // LOT 스캔 시: 스캔한 LOT 수량만, 아니면 전체 합계
  const displayStockQty = displayInfo.lotNo
    ? stockList
        .filter((item) => (item.lotNo ?? "") === displayInfo.lotNo)
        .reduce((sum, item) => sum + (item.stockQty ?? 0), 0)
    : stockList.reduce((sum, item) => sum + (item.stockQty ?? 0), 0);
  const { handleSearch } = PDA1060_R01Content();

  const [scanValue, setScanValue] = useState("");
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);

  const handleBarcodeScan = (barcode: string) => {
    const normalizedBarcode = barcode.toUpperCase();
    handleSearch(normalizedBarcode);
    setScanValue("");
  };

  const { scanInputRef, handleScanKeyDown, handleInput } = useBarcodeScan(
    handleBarcodeScan,
    {
      clearOnScan: true,
      refocusOnScan: true,
      debounceMs: 100,
      autoFocus: true,
    }
  );

  const handleDoubleTap = () => {
    const now = Date.now();
    const timeDiff = now - lastTapTime;

    if (timeDiff < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }

    setLastTapTime(now);
  };

  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  return (
    <div className="w-full bg-white p-4 pb-2 border-b border-gray-200 overflow-y-auto overflow-x-hidden">
      <div className="space-y-1">
        {/* SCAN */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">SCAN</label>
          <div className="flex-1 flex gap-1">
            <input
              ref={scanInputRef}
              type="text"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onInput={handleInput}
              onKeyDown={handleScanKeyDown}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? "text" : "none"}
              autoComplete="off"
              className="flex-1 max-w-[175px] px-2  text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="바코드 스캔"
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              className={`w-10 flex-shrink-0 px-2  border border-gray-300 rounded transition-colors ${
                keyboardEnabled
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title={keyboardEnabled ? "키보드 숨기기" : "키보드 표시"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </button>
          </div>
        </div>

        {/* 품목코드 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목코드</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={displayInfo.itemCd}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
              placeholder="품목코드"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 품목명 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={displayInfo.itemNm}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
              placeholder="품목명"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 규격 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">규격</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={displayInfo.itemSpec}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
              placeholder="규격"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 단위 및 등급 (1090과 동일 한 행 배치) */}
        <div className="flex items-center gap-1">
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">단위</label>
            <input
              type="text"
              value={displayInfo.unit}
              readOnly
              className="w-[50px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
          </div>
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-10 flex-shrink-0">등급</label>
            <input
              type="text"
              value={displayInfo.itemStatus}
              readOnly
              className="w-[50px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
          </div>
        </div>

        {/* 위치 (itemCd 기준 locationCd) */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">위치</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={displayInfo.locationCd || "-"}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
              placeholder="위치"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 스캔한 LOT */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">LOT</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={displayInfo.lotNo || "-"}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
              placeholder="스캔한 LOT"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 현재고 (테이블 수량 합계) */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">현재고</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={displayStockQty > 0 ? displayStockQty.toLocaleString() : ""}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDA1060_R01Info;
